import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { coachService, type CoachWithAcademy } from '@/services/coach';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { Building2, GraduationCap } from 'lucide-react';

function RedirectToCoachAcademy({ slug }: { slug: string }) {
    const navigate = useNavigate();
    useEffect(() => {
        navigate(`/academy/${slug}/coach/dashboard`, { replace: true });
    }, [navigate, slug]);
    return <p className="text-gray-600 dark:text-gray-400">Redirecting...</p>;
}

export default function CoachAcademyPicker() {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const [coachProfiles, setCoachProfiles] = useState<CoachWithAcademy[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!user || !user.roles?.includes('coach')) {
            navigate('/dashboard');
            return;
        }
        fetchCoachProfiles();
    }, [user, navigate]);

    const fetchCoachProfiles = async () => {
        try {
            setLoading(true);
            setError(null);
            const profiles = await coachService.getMyCoachProfiles();
            setCoachProfiles(profiles);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load your coaching profiles');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAcademy = (slug: string) => {
        navigate(`/academy/${slug}/coach/dashboard`);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-dashboard-canvas">
                <p className="text-gray-600 dark:text-gray-400">Loading your academies...</p>
            </div>
        );
    }

    // Auto-redirect if coach belongs to exactly one academy
    if (coachProfiles.length === 1 && coachProfiles[0].academySlug) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-dashboard-canvas">
                <RedirectToCoachAcademy slug={coachProfiles[0].academySlug} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dashboard-canvas py-12 px-4">
            <div className="max-w-2xl mx-auto">
                <div className="flex items-center gap-3 mb-2">
                    <GraduationCap className="h-8 w-8 text-primary-600" />
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Select an Academy
                    </h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-8">
                    Choose which academy you want to coach at.
                </p>

                {error && (
                    <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                        {error}
                    </div>
                )}

                {coachProfiles.length === 0 ? (
                    <Card>
                        <CardContent className="pt-6 text-center">
                            <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 dark:text-gray-400 mb-2">
                                You're not linked to any academies yet.
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-500">
                                Contact your academy owner to get set up.
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {coachProfiles.map((profile) => (
                            <Card
                                key={profile.id}
                                className="cursor-pointer hover:border-primary-500 transition-colors"
                                onClick={() => profile.academySlug && handleSelectAcademy(profile.academySlug)}
                            >
                                <CardHeader className="flex flex-row items-center gap-3">
                                    {profile.academyLogoUrl ? (
                                        <img
                                            src={profile.academyLogoUrl}
                                            alt={profile.academyName ?? 'Academy'}
                                            className="h-10 w-10 rounded-lg object-contain"
                                        />
                                    ) : (
                                        <Building2 className="h-10 w-10 text-primary-600" />
                                    )}
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-white">
                                            {profile.academyName ?? 'Unknown Academy'}
                                        </h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {profile.firstName} {profile.lastName}
                                        </p>
                                        {profile.specialties && profile.specialties.length > 0 && (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                                {profile.specialties.join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </CardHeader>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
